import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Building2, FileText, CheckCircle, ArrowRight, BookOpen, Mail, Calendar, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ConvocationAGModele = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Modèle Convocation AG Copropriété 2025 | Téléchargement gratuit</title>
        <meta name="description" content="Modèle gratuit de convocation d'assemblée générale de copropriété. Mentions obligatoires, délais, ordre du jour. Téléchargement PDF et Word." />
        <meta name="keywords" content="convocation AG copropriété, modèle convocation assemblée générale, ordre du jour AG, délai convocation AG copropriété" />
        <link rel="canonical" href="https://copropriete.app/convocation-ag-modele" />
      </Helmet>

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">copropriete.app</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => window.location.href = '/login'}>Se connecter</Button>
              <Button onClick={() => window.location.href = '/login'} className="bg-emerald-600 hover:bg-emerald-700 text-white">C'est gratuit</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-rose-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 bg-rose-100 text-rose-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Download className="w-4 h-4" />
              Modèle gratuit
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-6">Modèle de Convocation AG</h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Téléchargez notre modèle de convocation d'assemblée générale conforme à la législation en vigueur.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <main className="prose prose-slate prose-lg max-w-none">
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Calendar className="w-8 h-8 text-rose-600" />
              La convocation d'AG : règles essentielles
            </h2>
            <p>
              La <strong>convocation à l'assemblée générale</strong> est un document officiel qui doit respecter des règles strictes. Une convocation non conforme peut entraîner la <strong>nullité de toutes les décisions</strong> prises lors de l'AG.
            </p>

            <h3 className="text-xl font-bold text-slate-800">Délai de convocation</h3>
            <p>
              La convocation doit être envoyée <strong>au moins 21 jours</strong> avant la date de l'AG. Ce délai court à compter du lendemain de la première présentation de la lettre recommandée.
            </p>

            <div className="bg-rose-50 border-l-4 border-rose-500 p-6 rounded-r-xl my-8">
              <p className="text-rose-900 font-medium m-0">
                💡 <strong>Cas particulier :</strong> En cas d'urgence, le délai peut être réduit à 15 jours. Mais attention, la notion d'urgence doit être justifiée (sinuistre, travaux d'urgence...).
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <FileText className="w-8 h-8 text-rose-600" />
              Mentions obligatoires
            </h2>
            <p>La convocation doit obligatoirement contenir :</p>
            <ul>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" />Date, heure et lieu de l'assemblée</li>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" />Ordre du jour détaillé</li>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" />Texte des résolutions proposées</li>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" />Documents annexes (comptes, budget, devis...)</li>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" />Formulaire de pouvoir</li>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" />Modalités de vote par correspondance (si applicable)</li>
            </ul>
          </section>

          {/* Modèle */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Modèle de convocation</h2>
            
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 font-mono text-sm">
              <div className="text-center mb-8">
                <p className="font-bold text-lg">SYNDICAT DES COPROPRIÉTAIRES</p>
                <p>[Nom de la résidence]</p>
                <p>[Adresse de l'immeuble]</p>
              </div>
              
              <div className="mb-6">
                <p>[Nom du copropriétaire]</p>
                <p>[Adresse]</p>
              </div>
              
              <div className="text-right mb-6">
                <p>Lettre recommandée avec AR</p>
                <p>[Lieu], le [Date]</p>
              </div>
              
              <p className="font-bold mb-4 text-center text-lg">
                CONVOCATION À L'ASSEMBLÉE GÉNÉRALE [ORDINAIRE/EXTRAORDINAIRE]
              </p>
              
              <p className="mb-4">Madame, Monsieur,</p>
              
              <p className="mb-4">
                J'ai l'honneur de vous convoquer à l'assemblée générale [ordinaire/extraordinaire] 
                des copropriétaires de l'immeuble sis [adresse], qui se tiendra :
              </p>
              
              <div className="bg-white p-4 rounded border mb-4 text-center">
                <p className="font-bold">Le [DATE] à [HEURE]</p>
                <p>[LIEU - adresse complète]</p>
              </div>
              
              <p className="font-bold mb-2">ORDRE DU JOUR :</p>
              
              <ol className="mb-6 space-y-2">
                <li>1. Désignation du président de séance, du secrétaire et des scrutateurs</li>
                <li>2. Approbation des comptes de l'exercice clos le [date]</li>
                <li>3. Vote du quitus au syndic pour sa gestion</li>
                <li>4. Vote du budget prévisionnel de l'exercice [année]</li>
                <li>5. Désignation du syndic</li>
                <li>6. Élection des membres du conseil syndical</li>
                <li>7. [Autres résolutions...]</li>
                <li>X. Questions diverses</li>
              </ol>
              
              <p className="mb-4">
                Vous trouverez ci-joints les documents suivants :
              </p>
              <ul className="mb-4">
                <li>- Projet de résolutions</li>
                <li>- Comptes de l'exercice écoulé</li>
                <li>- Budget prévisionnel</li>
                <li>- Formulaire de pouvoir</li>
                <li>- [Autres documents...]</li>
              </ul>
              
              <p className="mb-4">
                Si vous ne pouvez assister à cette assemblée, vous pouvez vous faire représenter 
                par un mandataire de votre choix (copropriétaire ou tiers) en utilisant le formulaire 
                de pouvoir ci-joint.
              </p>
              
              <p className="mb-4">
                Je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées.
              </p>
              
              <div className="mt-8">
                <p>Le Syndic</p>
                <p>[Nom et signature]</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Mail className="w-8 h-8 text-rose-600" />
              Envoi de la convocation
            </h2>
            
            <h3 className="text-xl font-bold text-slate-800">Mode d'envoi</h3>
            <p>
              La convocation doit être envoyée par <strong>lettre recommandée avec accusé de réception</strong>. 
              Toutefois, les copropriétaires peuvent accepter de recevoir la convocation par email 
              avec accusé de réception électronique.
            </p>

            <h3 className="text-xl font-bold text-slate-800">Qui reçoit la convocation ?</h3>
            <ul>
              <li>Tous les copropriétaires</li>
              <li>Les usufruitiers (pour les décisions relevant de l'article 24)</li>
              <li>Le conseil syndical</li>
            </ul>

            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl my-8">
              <p className="text-emerald-900 font-medium m-0">
                ✅ <strong>Avec copropriete.app</strong>, générez automatiquement vos convocations d'AG avec ordre du jour, documents annexes et formulaire de pouvoir. Envoyez-les en un clic !
              </p>
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 my-8 border border-rose-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Simplifiez l'organisation de vos AG</h3>
            <p className="text-slate-700 mb-6">
              copropriete.app génère automatiquement vos convocations conformes et gère tout le processus d'AG pour vous.
            </p>
            <Button onClick={() => window.location.href = '/login'} size="lg" className="bg-rose-600 hover:bg-rose-700">
              Essayer gratuitement pendant 7 jours
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">copropriete.app</span>
          </Link>
          <p className="text-sm">© 2025 copropriete.app. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default ConvocationAGModele;
